import { useCountdown } from "@/hooks/useCountdown";
import { getOrderDeadline } from "@/lib/orderDeadline";
import { cn } from "@/lib/utils";

interface OrderCountdownProps {
  variant: "hero" | "compact";
  theme?: "light" | "dark";
  deadline?: Date;
  className?: string;
}

const pad = (n: number) => n.toString().padStart(2, "0");
const DEFAULT_DEADLINE = getOrderDeadline();

export default function OrderCountdown({
  variant,
  theme = "light",
  deadline = DEFAULT_DEADLINE,
  className,
}: OrderCountdownProps) {
  const isDark = theme === "dark";
  const { days, hours, minutes, seconds, isExpired } = useCountdown(deadline);

  if (isExpired) {
    return (
      <div
        data-variant={variant}
        className={cn(
          "rounded-2xl border border-border bg-card text-center",
          variant === "hero" ? "px-6 py-5" : "px-4 py-3",
          className,
        )}
      >
        <p
          className={cn(
            "font-bold text-foreground",
            variant === "hero" ? "text-lg" : "text-sm",
          )}
        >
          Orders for 2026 are now closed
        </p>
        <p
          className={cn(
            "text-muted-foreground mt-1",
            variant === "hero" ? "text-sm" : "text-xs",
          )}
        >
          Thank you for your trust. See you next year, insyaAllah.
        </p>
      </div>
    );
  }

  const boxes: Array<{ label: string; value: number; testId: string }> = [
    { label: "DAYS", value: days, testId: "countdown-days" },
    { label: "HRS", value: hours, testId: "countdown-hours" },
    { label: "MIN", value: minutes, testId: "countdown-minutes" },
    { label: "SEC", value: seconds, testId: "countdown-seconds" },
  ];

  return (
    <div data-variant={variant} className={cn("text-center", className)}>
      <div
        className={cn(
          "grid grid-cols-4",
          variant === "hero" ? "gap-2" : "gap-1.5 max-w-xs mx-auto",
        )}
      >
        {boxes.map((box) => (
          <div
            key={box.label}
            className={cn(
              "rounded-xl border",
              isDark
                ? "border-white/25 bg-white/10 backdrop-blur"
                : "border-primary/20 bg-card",
              variant === "hero" ? "px-2 py-3" : "px-1.5 py-2",
            )}
          >
            <div
              data-testid={box.testId}
              className={cn(
                "font-extrabold tabular-nums leading-none",
                isDark ? "text-white" : "text-primary",
                variant === "hero" ? "text-3xl" : "text-xl",
              )}
            >
              {pad(box.value)}
            </div>
            <div
              className={cn(
                "font-bold tracking-widest mt-1",
                isDark ? "text-white/70" : "text-muted-foreground",
                variant === "hero" ? "text-[10px]" : "text-[9px]",
              )}
            >
              {box.label}
            </div>
          </div>
        ))}
      </div>
      <p
        className={cn(
          "mt-2",
          isDark ? "text-white/80" : "text-muted-foreground",
          variant === "hero" ? "text-sm" : "text-xs",
        )}
      >
        Orders close{" "}
        <span
          className={cn(
            "font-semibold",
            isDark ? "text-white" : "text-foreground",
          )}
        >
          20 May, Wednesday, 1:00 AM
        </span>
      </p>
    </div>
  );
}
